<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import { ExclamationCircleIcon, QuestionMarkCircleIcon, CheckIcon } from '@heroicons/vue/20/solid';
import { parse, format, isDate, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';

const props = defineProps<{
  label: string;
  description?: string;
  icon?: any;
  tooltip?: string;
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', datetime?: string | null): void
}>();

const dateInput = ref("");
const hasError = ref(false);
const showTooltip = ref(false);

const inputClasses = computed(() => {
  if (isValid(parsedDate.value)) return "block w-full rounded-md border-0 py-1.5 placeholder-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
  return hasError.value 
    ? "block w-full rounded-md border-0 py-1.5 pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500 sm:text-sm sm:leading-6"
    : "block w-full rounded-md border-0 py-1.5 pr-10 placeholder-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6";
});

const validateDate = () => {
  if (dateInput.value.length < 16) hasError.value = false 
  else hasError.value = !isValid(parsedDate.value)
}

const parsedDate = computed(() => {
  const formatString = "dd/MM/yyyy HH:mm";
  const date = parse(dateInput.value, formatString, new Date());
  return date;
});

watch(parsedDate, (newValue) => emits("update:modelValue", dateInput.value))
</script>
<template>
  <div class="text-left">
    <label for="date" class="block text-sm font-medium leading-6 text-gray-900">{{ props.label }} :</label>
    <span v-if="props.description" class="block text-xs italic text-slate-600">({{ props.description }})</span>
    <div class="relative mt-2 rounded-md shadow-sm">
      <input 
        @focusin="showTooltip = true"
        @focusout="showTooltip = false"
        @keydown="showTooltip = false"
        v-model="dateInput" 
        type="text"
        :class="inputClasses"
        @input="validateDate"
        :aria-invalid="hasError"
        placeholder="25/12/2023 19:00"
        class="block w-full rounded-md border-0 py-1.5 pl-9 pr-20 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
      />
      <div v-if="isValid(parsedDate)" class="absolute inset-y-0 z-10 flex items-center pointer-events-none right-2">
        <CheckIcon class="w-5 h-5 text-green-600" aria-hidden="true" />
      </div>
      <div
        v-else
        @mouseover="showTooltip = true"
        v-tooltip.bottom="{
          shown: showTooltip,
          content: tooltip,
          triggers: ['hover', 'focus'],
        }" class="absolute inset-y-0 flex items-center pointer-events-none right-2">
        <QuestionMarkCircleIcon class="w-5 h-5 text-slate-400" aria-hidden="true" />
      </div>
      <div v-if="hasError" class="absolute inset-y-0 flex items-center pr-3 pointer-events-none right-5">
        <ExclamationCircleIcon class="w-5 h-5 text-red-500" aria-hidden="true" />
      </div>
      <div v-if="icon" class="absolute inset-y-0 flex items-center pr-3 pointer-events-none left-2">
        <component :is="icon" class="w-5 h-5 text-slate-400" aria-hidden="true" />
      </div>
    </div>
    <p v-if="hasError" class="mt-2 text-sm text-red-600" id="price-error">Merci d'entrer une date au format dd/MM/yyyy HH:mm.</p>
    <div v-if="parsedDate && isValid(parsedDate)" class="pl-1 my-2 italic">
      Le {{ format(parsedDate, "d MMMM 'à' HH'h'mm", { locale: fr }) }}
    </div>
  </div>
</template>
